import React, { useState } from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../common/ui';
import { fmtDate } from '../../utils/format';
import { ACCEPT_ANY, ACCEPT_IMAGE, PdfRow, PhotoThumb, useFilePicker } from './files';

/**
 * Every file of a job (general, per step and per visit) or of a quote: PDFs and drawings (DWG/DXF) as lists,
 * photos as a grid.
 * `owner` is the job id or `quoteOwner(id)` (see files.js).
 */
export default function FilesCard({ owner, files, visits = [], upload, progress, openFile, askDelete }) {
    const { t } = useTranslation();
    const [dragOver, setDragOver] = useState(false);
    const [fileInput, pickFiles] = useFilePicker((fl) => upload(fl), { accept: ACCEPT_ANY });
    const [cameraInput, takePhoto] = useFilePicker((fl) => upload(fl), { accept: ACCEPT_IMAGE, capture: true });
    const uploading = progress !== null;

    const pdfs = files.filter(f => f.kind === 'PDF');
    const drawings = files.filter(f => f.kind === 'DRAWING');
    const sheets = files.filter(f => f.kind === 'EXCEL');
    const docs = files.filter(f => f.kind === 'WORD');
    const photos = files.filter(f => f.kind === 'IMAGE');
    const visitDate = Object.fromEntries(visits.map(v => [v.id, v.visitDate]));
    const labelOf = (f) => f.stepCode ? t(`steps.${f.stepCode}`)
        : f.visitId ? t('files.forVisit', { date: fmtDate(visitDate[f.visitId]) }) : null;

    const sub = (text) => <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>{text}</Typography>;

    return (
        <SectionCard title={t('files.title')}
                     action={(
                         <Box sx={{ display: 'flex', gap: 1 }}>
                             <Button size="small" startIcon={<PhotoCameraOutlinedIcon />} onClick={takePhoto} disabled={uploading}>{t('files.takePhoto')}</Button>
                             <Button size="small" variant="outlined" startIcon={<UploadFileOutlinedIcon />} onClick={pickFiles} disabled={uploading}>{t('files.addFile')}</Button>
                         </Box>
                     )}>
            {fileInput}{cameraInput}
            {uploading && <LinearProgress variant="determinate" value={progress} />}
            <Box onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                 onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
                 sx={{ p: 2.5, bgcolor: dragOver ? 'rgba(66,120,152,0.06)' : 'transparent', transition: 'background 0.15s' }}>
                {!files.length ? (
                    <Box onClick={() => !uploading && pickFiles()} sx={{
                        py: 5, px: 3, textAlign: 'center', borderRadius: '14px', cursor: 'pointer',
                        border: `2px dashed ${dragOver ? '#427898' : '#D5DEE7'}`, bgcolor: '#FAFBFC', '&:hover': { borderColor: '#427898' },
                    }}>
                        <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: dragOver ? '#427898' : '#CBD5E1', mb: 1 }} />
                        <Typography sx={{ fontWeight: 700, color: '#334155' }}>{t('files.empty')}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#94A3B8', mt: 0.5 }}>{t('files.dropHint')}</Typography>
                    </Box>
                ) : (
                    <>
                        {pdfs.length > 0 && (
                            <Box sx={{ mb: drawings.length || sheets.length || docs.length || photos.length ? 2.5 : 0 }}>
                                {sub(t('files.documents'))}
                                {pdfs.map(f => <PdfRow key={f.id} owner={owner} file={f} label={labelOf(f)} onOpen={() => openFile([f], 0)} onDelete={askDelete ? () => askDelete(f) : undefined} />)}
                            </Box>
                        )}
                        {drawings.length > 0 && (
                            <Box sx={{ mb: sheets.length || docs.length || photos.length ? 2.5 : 0 }}>
                                {sub(t('files.drawings'))}
                                {drawings.map(f => <PdfRow key={f.id} owner={owner} file={f} label={labelOf(f)} onDelete={askDelete ? () => askDelete(f) : undefined} />)}
                            </Box>
                        )}
                        {sheets.length > 0 && (
                            <Box sx={{ mb: docs.length || photos.length ? 2.5 : 0 }}>
                                {sub(t('files.sheets'))}
                                {sheets.map(f => <PdfRow key={f.id} owner={owner} file={f} label={labelOf(f)} onDelete={askDelete ? () => askDelete(f) : undefined} />)}
                            </Box>
                        )}
                        {docs.length > 0 && (
                            <Box sx={{ mb: photos.length ? 2.5 : 0 }}>
                                {sub(t('files.wordDocs'))}
                                {docs.map(f => <PdfRow key={f.id} owner={owner} file={f} label={labelOf(f)} onDelete={askDelete ? () => askDelete(f) : undefined} />)}
                            </Box>
                        )}
                        {photos.length > 0 && (
                            <Box>
                                {sub(t('files.photos'))}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                    {photos.map((f, i) => <PhotoThumb key={f.id} owner={owner} file={f} onOpen={() => openFile(photos, i)} />)}
                                </Box>
                            </Box>
                        )}
                        <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 2 }}>{t('files.dropHint')}</Typography>
                    </>
                )}
            </Box>
        </SectionCard>
    );
}
